import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, ChartLine, Target, Check } from "lucide-react";
import BrandHeart from "./BrandHeart";
import { useCookieConsent } from "../../context/CookieContext";

const CATEGORIES = [
  {
    key: "necessary",
    label: "Notwendige Cookies",
    description:
      "Diese Cookies sind für den Betrieb der Seite zwingend erforderlich — z. B. für Login-Sitzungen, den Warenkorb und Sicherheitsfunktionen. Sie können nicht deaktiviert werden.",
    icon: ShieldCheck,
    required: true,
  },
  {
    key: "analytics",
    label: "Statistik & Analyse",
    description:
      "Anonymisierte Messungen helfen uns zu verstehen, wie Menschen QHearts nutzen — damit wir die Erfahrung Schritt für Schritt besser machen können. Keine personenbezogene Werbung.",
    icon: ChartLine,
    required: false,
  },
  {
    key: "marketing",
    label: "Marketing & Tracking",
    description:
      "Ermöglicht personalisierte Inhalte und Werbung außerhalb von QHearts — nur mit deiner ausdrücklichen Zustimmung.",
    icon: Target,
    required: false,
  },
];

export default function CookieBanner() {
  const {
    consent,
    hasDecided,
    settingsOpen,
    openSettings,
    closeSettings,
    decide,
    acceptAll,
    rejectAll,
  } = useCookieConsent();

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // Defer mount a tick so hydration is clean
    const t = setTimeout(() => setMounted(true), 120);
    return () => clearTimeout(t);
  }, []);

  const show = mounted && (!hasDecided || settingsOpen);

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop only when settings modal is open */}
          {settingsOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
              onClick={closeSettings}
            />
          )}

          {settingsOpen ? (
            <SettingsModal
              consent={consent}
              onClose={closeSettings}
              onSave={decide}
              onAcceptAll={acceptAll}
              onRejectAll={rejectAll}
            />
          ) : (
            <BottomBar
              onAcceptAll={acceptAll}
              onRejectAll={rejectAll}
              onManage={openSettings}
            />
          )}
        </>
      )}
    </AnimatePresence>
  );
}

function BottomBar({ onAcceptAll, onRejectAll, onManage }) {
  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="fixed bottom-0 left-0 right-0 z-[55] px-4 sm:px-6 pb-4 sm:pb-6"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-Einstellungen"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-5xl rounded-3xl bg-white/95 backdrop-blur-xl border border-qh-line shadow-[0_30px_60px_-20px_rgba(10,10,10,0.25)] overflow-hidden">
        <div className="p-5 sm:p-7 lg:p-8 flex flex-col lg:flex-row gap-5 lg:gap-8 lg:items-center">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="h-12 w-12 rounded-2xl bg-[#FFE4E6] flex items-center justify-center shrink-0">
              <BrandHeart className="w-7 h-7" alt="" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-qh-rose">
                Cookies
              </div>
              <h2 className="mt-1 font-display text-lg sm:text-xl font-medium leading-snug text-qh-ink">
                Ein kurzer Moment Transparenz.
              </h2>
              <p className="mt-2 text-sm text-qh-slate leading-relaxed">
                Wir verwenden Cookies, um dir ein sicheres und personalisiertes
                Erlebnis zu bieten. Einige Cookies sind notwendig, während
                andere uns helfen, diese Website zu verbessern.{" "}
                <Link
                  to="/datenschutz"
                  className="text-qh-rose hover:text-qh-rosedark underline-offset-2 hover:underline"
                  data-testid="cookie-privacy-link"
                >
                  Mehr in der Datenschutzerklärung
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 lg:shrink-0 lg:w-auto">
            <button
              onClick={onManage}
              className="order-3 sm:order-1 rounded-full border border-qh-line bg-white hover:bg-[#FFFBFB] text-qh-ink text-sm font-medium h-11 px-5 transition-colors whitespace-nowrap"
              data-testid="cookie-manage"
            >
              Einstellungen verwalten
            </button>
            <button
              onClick={onRejectAll}
              className="order-2 rounded-full border border-qh-line bg-white hover:bg-[#FFFBFB] text-qh-ink text-sm font-medium h-11 px-5 transition-colors whitespace-nowrap"
              data-testid="cookie-reject"
            >
              Nur notwendige Cookies
            </button>
            <button
              onClick={onAcceptAll}
              className="order-1 sm:order-3 rounded-full bg-qh-rose hover:bg-qh-rosedark text-white text-sm font-medium h-11 px-6 transition-all hover:-translate-y-0.5 whitespace-nowrap"
              data-testid="cookie-accept"
            >
              Alle akzeptieren
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function SettingsModal({ consent, onClose, onSave, onAcceptAll, onRejectAll }) {
  const [draft, setDraft] = useState({
    necessary: true,
    analytics: consent?.analytics || false,
    marketing: consent?.marketing || false,
  });

  const toggle = (key) => {
    if (key === "necessary") return;
    setDraft((p) => ({ ...p, [key]: !p[key] }));
  };

  const save = () => onSave(draft);

  return (
    <motion.div
      initial={{ y: 20, opacity: 0, scale: 0.98 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: 20, opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center p-0 sm:p-5 pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-settings-title"
    >
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl border border-qh-line shadow-[0_40px_80px_-20px_rgba(10,10,10,0.35)] pointer-events-auto max-h-[92vh] flex flex-col"
        data-testid="cookie-settings-modal"
      >
        <div className="flex items-start justify-between p-6 sm:p-8 border-b border-qh-line">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-qh-rose">
              Cookie-Einstellungen
            </div>
            <h2
              id="cookie-settings-title"
              className="mt-2 font-display text-2xl sm:text-3xl font-medium leading-tight"
            >
              Du entscheidest,{" "}
              <span className="italic text-qh-rose">was wir sehen.</span>
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="h-10 w-10 rounded-full border border-qh-line bg-white flex items-center justify-center shrink-0 hover:bg-[#FFFBFB] transition-colors"
            data-testid="cookie-settings-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-4">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const on = cat.required ? true : draft[cat.key];
            return (
              <div
                key={cat.key}
                className={`rounded-2xl border p-5 transition-colors ${
                  on
                    ? "border-qh-rose/40 bg-[#FFF1F3]"
                    : "border-qh-line bg-[#FFFBFB]"
                }`}
                data-testid={`cookie-category-${cat.key}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                        on ? "bg-qh-rose text-white" : "bg-white border border-qh-line text-qh-slate"
                      }`}
                    >
                      <Icon className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-lg font-medium text-qh-ink">
                          {cat.label}
                        </h3>
                        {cat.required && (
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-qh-slate px-2 py-0.5 rounded-full bg-white border border-qh-line">
                            Immer aktiv
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 text-sm text-qh-slate leading-relaxed">
                        {cat.description}
                      </p>
                    </div>
                  </div>
                  <Toggle
                    checked={on}
                    disabled={cat.required}
                    onChange={() => toggle(cat.key)}
                    testId={`cookie-toggle-${cat.key}`}
                  />
                </div>
              </div>
            );
          })}

          <p className="text-xs text-qh-slate leading-relaxed pt-2">
            Du kannst deine Entscheidung jederzeit widerrufen — im Footer unter{" "}
            <strong className="text-qh-ink font-medium">
              „Cookie-Einstellungen ändern"
            </strong>
            . Details findest du in unserer{" "}
            <Link
              to="/datenschutz"
              onClick={onClose}
              className="text-qh-rose hover:text-qh-rosedark underline-offset-2 hover:underline"
            >
              Datenschutzerklärung
            </Link>
            {" "}und in den{" "}
            <Link
              to="/agb"
              onClick={onClose}
              className="text-qh-rose hover:text-qh-rosedark underline-offset-2 hover:underline"
            >
              AGB
            </Link>
            .
          </p>
        </div>

        <div className="px-6 sm:px-8 py-5 border-t border-qh-line flex flex-col sm:flex-row gap-2.5 bg-white">
          <button
            onClick={onRejectAll}
            className="order-3 sm:order-1 rounded-full border border-qh-line bg-white hover:bg-[#FFFBFB] text-qh-ink text-sm font-medium h-11 px-5 transition-colors"
            data-testid="cookie-settings-reject"
          >
            Nur notwendige
          </button>
          <button
            onClick={save}
            className="order-2 rounded-full border border-qh-line bg-white hover:bg-[#FFFBFB] text-qh-ink text-sm font-medium h-11 px-5 transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid="cookie-settings-save"
          >
            <Check className="w-4 h-4" /> Auswahl speichern
          </button>
          <button
            onClick={onAcceptAll}
            className="order-1 sm:order-3 flex-1 sm:flex-none rounded-full bg-qh-rose hover:bg-qh-rosedark text-white text-sm font-medium h-11 px-6 transition-all hover:-translate-y-0.5"
            data-testid="cookie-settings-accept-all"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function Toggle({ checked, disabled, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative h-7 w-12 rounded-full border transition-colors shrink-0 ${
        checked
          ? "bg-qh-rose border-qh-rose"
          : "bg-white border-qh-line"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      data-testid={testId}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
